import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast, toastErrorMessage } from "../lib/toast";
import Sheet from "../components/Sheet";
import DateStampedEntry from "../components/DateStampedEntry";
import CompleteTaskModal from "../components/CompleteTaskModal";
import TaskEditModal from "../components/TaskEditModal";
import TimePicker from "../components/TimePicker";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { TierTooltip } from "../lib/tierLegend";
import {
  Phone, Mail, MapPin, Plus, Archive, ArchiveRestore, Edit3, Check, Linkedin, Facebook, Download,
} from "lucide-react";
import {
  fmtAestDateTime, fmtAestDate, fmtAud, fmtPhone, telHref, mapsHref, segmentsTheme, todayAestISO,
} from "../lib/format";
import { TASK_TYPES, taskTypePillClass } from "../lib/taskTypes";

const TABS = ["Details", "Contacts", "Specifics", "Discovery", "Equipment", "Activity", "Quotes"];

function normaliseUrl(u) {
  if (!u) return "";
  const s = String(u).trim();
  if (!s) return "";
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

export default function CompanyProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, meta } = useAuth();
  const toast = useToast();
  const [co, setCo] = useState(null);
  // Allow deep-linking to a tab via ?tab=Contacts (case-insensitive)
  const initialTab = (() => {
    const t = searchParams.get("tab");
    if (!t) return "Details";
    const match = TABS.find(x => x.toLowerCase() === t.toLowerCase());
    return match || "Details";
  })();
  const [tab, setTab] = useState(initialTab);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showQuickTask, setShowQuickTask] = useState(false);
  const [quickContactId, setQuickContactId] = useState(null);
  const [tasksVersion, setTasksVersion] = useState(0);

  async function reload() {
    const { data } = await api.get(`/companies/${id}`);
    setCo(data);
  }
  async function reloadContacts() {
    const { data } = await api.get(`/companies/${id}/contacts`);
    setContacts(data);
  }
  useEffect(() => { reload(); reloadContacts(); api.get("/users").then(r => setUsers(r.data)); }, [id]);

  const theme = useMemo(() => segmentsTheme(co?.product_segments || []), [co]);

  if (!co) return <div className="skeleton h-40 w-full"/>;
  const archived = co.archived;

  return (
    <div data-theme={theme === "default" ? undefined : theme} className="space-y-4" data-testid="company-profile">
      <div className="flex flex-col gap-2">
        <Link to="/companies" className="text-sm text-gray-500 hover:text-black">← Companies</Link>
        <div className="flex flex-wrap items-start gap-3 justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{co.company_name}</h1>
            <div className="text-sm text-gray-500 flex flex-wrap items-center gap-2 mt-1">
              <span className="pill pill-grey">Tier {co.customer_tier}</span>
              <span>{co.state}</span>
              {co.country && <span>· {co.country}</span>}
              {(co.product_segments || []).map(s => {
                const isM = s === "M";
                return <span key={s} className={`pill ${isM ? "pill-grey" : "pill-green"}`}>{s}</span>;
              })}
              {archived && <span className="pill pill-red">Archived</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="cesco-btn-ghost flex items-center gap-2"
              onClick={async()=>{
                try {
                  const res = await api.get(`/companies/${co.id}/discovery.csv`, { responseType: "blob" });
                  const cd = res.headers["content-disposition"] || "";
                  const m = cd.match(/filename="([^"]+)"/);
                  const filename = m ? m[1] : `${(co.company_name||"company").replace(/[^a-z0-9._-]+/gi,"-")}-discovery.csv`;
                  const url = window.URL.createObjectURL(res.data);
                  const a = document.createElement("a"); a.href = url; a.download = filename;
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) { toast.error(toastErrorMessage(e)); }
              }}
              data-testid="btn-export-discovery">
              <Download size={14}/> Export Discovery
            </button>
            {user?.role === "admin" && (
              archived ? (
                <button className="cesco-btn-ghost flex items-center gap-2"
                  onClick={async()=>{ await api.post(`/companies/${id}/restore`); toast.success("Company restored"); reload(); }}
                  data-testid="btn-restore">
                  <ArchiveRestore size={14}/> Restore
                </button>
              ) : (
                <button className="cesco-btn-ghost flex items-center gap-2"
                  onClick={async()=>{ if(confirm("Archive this company?")) { await api.post(`/companies/${id}/archive`); toast.success("Company archived"); reload(); } }}
                  data-testid="btn-archive">
                  <Archive size={14}/> Archive
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <div className="tabbar" data-testid="company-tabs">
        {TABS.map(t => (
          <button key={t} className={tab === t ? "active" : ""}
            onClick={() => { setTab(t); setSearchParams({ tab: t }, { replace: true }); }}
            data-testid={`tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>

      <div className="pt-2">
        {tab === "Details"    && <Details co={co} meta={meta} users={users} reload={reload} toast={toast} />}
        {tab === "Contacts"   && <Contacts companyId={id} meta={meta} toast={toast}
                                            contacts={contacts} reloadContacts={reloadContacts}
                                            users={users}
                                            tasksVersion={tasksVersion}
                                            autoOpenNew={searchParams.get("new_contact") === "1"}
                                            onAutoOpenConsumed={() => {
                                              const sp = new URLSearchParams(searchParams);
                                              sp.delete("new_contact");
                                              setSearchParams(sp, { replace: true });
                                            }}
                                            onQuickTask={(c)=>{ setQuickContactId(c.id); setShowQuickTask(true); }} />}
        {tab === "Specifics"  && <Specifics co={co} meta={meta} reload={reload} toast={toast} />}
        {tab === "Discovery"  && <Discovery co={co} meta={meta} toast={toast} />}
        {tab === "Equipment"  && <Equipment companyId={id} toast={toast} />}
        {tab === "Activity"   && <Activity companyId={id} users={users} toast={toast} contacts={contacts} />}
        {tab === "Quotes"     && <CompanyQuotes companyId={id} />}
      </div>

      {/* Persistent floating + Add task button - visible on all tabs */}
      {!archived && (
        <button
          onClick={() => { setQuickContactId(null); setShowQuickTask(true); }}
          className="fixed bottom-5 right-5 z-40 cesco-btn-primary rounded-full shadow-lg px-5 py-3 flex items-center gap-2"
          data-testid="fab-add-task">
          <Plus size={18}/> <span className="hidden sm:inline">Add task</span>
        </button>
      )}

      {showQuickTask && (
        <TaskForm
          companyId={id}
          users={users}
          contacts={contacts}
          presetContactId={quickContactId}
          onClose={() => { setShowQuickTask(false); setQuickContactId(null); }}
          onSaved={() => { setTasksVersion(v => v + 1); }}
          toast={toast}
        />
      )}
    </div>
  );
}

// --- Tab: Details ----------------------------------------------------------
function Details({ co, meta, users, reload, toast }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ ...co });
  useEffect(() => { setF({ ...co }); }, [co]);

  async function save() {
    const updates = {};
    ["company_name","state","customer_tier","phone","website","street","suburb","postcode",
     "major_town","area_state","country","generic_email","current_customer","owns_equipment",
     "tier_reason_notes","type_of_contact","assigned_rep_id"
    ].forEach(k => updates[k] = f[k] ?? null);
    updates.company_name = (updates.company_name || "").trim();
    if (!updates.company_name) {
      toast.error("Company name is required.");
      return;
    }
    updates.customer_tier = Number(updates.customer_tier);
    try {
      await api.patch(`/companies/${co.id}`, updates);
      toast.success("Company details saved");
      setEdit(false); reload();
    } catch(e) { toast.error(toastErrorMessage(e)); }
  }

  return (
    <div className="space-y-4">
      <div className="cesco-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Company details</h3>
          {!edit ? (
            <button className="cesco-btn-ghost flex items-center gap-2" onClick={()=>setEdit(true)}
                    data-testid="btn-edit-details"><Edit3 size={14}/> Edit</button>
          ) : (
            <div className="flex gap-2">
              <button className="cesco-btn-ghost" onClick={()=>{setEdit(false); setF({...co});}}>Cancel</button>
              <button className="cesco-btn-primary flex items-center gap-2" onClick={save}
                      data-testid="btn-save-details"><Check size={14}/> Save</button>
            </div>
          )}
        </div>
        {!edit ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <InfoRow label="Tier reason notes" full>{co.tier_reason_notes || "-"}</InfoRow>
            <InfoRow label="Company Phone Number">{co.phone ? <a className="text-blue-700 hover:underline" href={telHref(co.phone)}><Phone size={12} className="inline mr-1"/>{fmtPhone(co.phone)}</a> : "-"}</InfoRow>
            <InfoRow label="Website">{co.website ? <a className="text-blue-700 hover:underline" href={co.website.startsWith("http") ? co.website : `https://${co.website}`} target="_blank" rel="noreferrer">{co.website}</a> : "-"}</InfoRow>
            <InfoRow label="Address" full>{[co.street, co.suburb, co.postcode, co.state].filter(Boolean).join(", ") || "-"}{co.street &&
              <a className="ml-2 text-blue-700 hover:underline" href={mapsHref([co.street, co.suburb, co.postcode, co.state, co.country])} target="_blank" rel="noreferrer"><MapPin size={12} className="inline mr-1"/>Open in Maps</a>}</InfoRow>
            <InfoRow label="Major Town">{co.major_town || "-"}</InfoRow>
            <InfoRow label="Area State">{co.area_state || "-"}</InfoRow>
            <InfoRow label="Generic Email">{co.generic_email ? <a className="text-blue-700 hover:underline" href={`mailto:${co.generic_email}`}><Mail size={12} className="inline mr-1"/>{co.generic_email}</a> : "-"}</InfoRow>
            <InfoRow label="Country">{co.country || "-"}</InfoRow>
            <InfoRow label="Current customer">{co.current_customer || "-"}</InfoRow>
            <InfoRow label="Owns our equipment">{co.owns_equipment || "-"}</InfoRow>
            <InfoRow label="Type of contact">{co.type_of_contact || "-"}</InfoRow>
            <InfoRow label="Assigned rep">{users.find(u=>u.id === (co.assigned_rep_id || co.created_by_id))?.name || "-"}</InfoRow>
            <InfoRow label="Product Segments" full>
              {(co.product_segments || []).length ? (
                <span className="flex flex-wrap gap-1">
                  {(co.product_segments || []).map(s => (
                    <span key={s} className={`pill ${s === "M" ? "pill-grey" : "pill-green"}`}>{s}</span>
                  ))}
                </span>
              ) : "-"}
            </InfoRow>
            {(co.product_segments || []).includes("M") && (
              <InfoRow label="Market Segment (Mixer)">
                {(() => {
                  const s = meta?.mixer_market_segments?.find(x => x.code === co.mixer_market_segment);
                  return co.mixer_market_segment ? (s ? `${s.code} - ${s.name}` : co.mixer_market_segment) : "-";
                })()}
              </InfoRow>
            )}
            {(co.product_segments || []).includes("D") && (
              <InfoRow label="Market Segment (Drill)">
                {(() => {
                  const s = meta?.drill_market_segments?.find(x => x.code === co.drill_market_segment);
                  return co.drill_market_segment ? (s ? `${s.code} - ${s.name}` : co.drill_market_segment) : "-";
                })()}
              </InfoRow>
            )}
          </dl>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company name *" full><input className="cesco-input" value={f.company_name||""} required minLength={1} onChange={e=>setF({...f,company_name:e.target.value})}/></Field>
            <Field label="State *">
              {(!f.country || f.country === "Australia") ? (
                <select className="cesco-input" value={f.state||""} onChange={e=>setF({...f, state:e.target.value})}>
                  {meta?.au_states.map(s => <option key={s.code} value={s.code}>{`${s.code} - ${s.name}`}</option>)}
                </select>
              ) : (
                <input className="cesco-input" value={f.state||""} required maxLength={40}
                       placeholder="State / region"
                       onChange={e=>setF({...f, state:e.target.value})}/>
              )}
            </Field>
            <Field label="Customer Tier *" titleExtra={<TierTooltip testId="edit-tier-tooltip"/>}>
              <select className="cesco-input" value={f.customer_tier||""} onChange={e=>setF({...f, customer_tier:e.target.value})}>
                {meta?.customer_tiers.map(t => <option key={t.value} value={t.value}>{t.value}</option>)}
              </select>
            </Field>
            <Field label="Tier reason notes (in own words)" full>
              <textarea className="cesco-input min-h-[80px]" rows={3} maxLength={500}
                        placeholder="Add free-text context for this company's tier (e.g. lost the Bates account in March, AP team rotating, etc.)"
                        value={f.tier_reason_notes||""}
                        onChange={e=>setF({...f, tier_reason_notes:e.target.value})}
                        data-testid="tier-reason-notes-input"/>
            </Field>
            <Field label="Company Phone Number"><input className="cesco-input" value={f.phone||""} onChange={e=>setF({...f, phone:e.target.value})}/></Field>
            <Field label="Website"><input className="cesco-input" value={f.website||""} maxLength={200} onChange={e=>setF({...f, website:e.target.value})}/></Field>
            <Field label="Street"><input className="cesco-input" value={f.street||""} maxLength={80} onChange={e=>setF({...f, street:e.target.value})}/></Field>
            <Field label="Suburb"><input className="cesco-input" value={f.suburb||""} maxLength={60} onChange={e=>setF({...f, suburb:e.target.value})}/></Field>
            <Field label="Postcode"><input className="cesco-input" value={f.postcode||""} maxLength={6} onChange={e=>setF({...f, postcode:e.target.value})}/></Field>
            <Field label="Major Town"><input className="cesco-input" value={f.major_town||""} maxLength={60} onChange={e=>setF({...f, major_town:e.target.value})}/></Field>
            <Field label="Area State"><input className="cesco-input" value={f.area_state||""} maxLength={30} onChange={e=>setF({...f, area_state:e.target.value})}/></Field>
            <Field label="Country">
              <select className="cesco-input" value={f.country||"Australia"} onChange={e=>setF({...f, country:e.target.value})}>
                {meta?.countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Generic Email"><input className="cesco-input" value={f.generic_email||""} maxLength={254} onChange={e=>setF({...f, generic_email:e.target.value})}/></Field>
            <Field label="Current Customer">
              <select className="cesco-input" value={f.current_customer||""} onChange={e=>setF({...f, current_customer:e.target.value})}>
                <option value="">-</option><option>Yes</option><option>No</option>
              </select>
            </Field>
            <Field label="Owns our equipment">
              <select className="cesco-input" value={f.owns_equipment||""} onChange={e=>setF({...f, owns_equipment:e.target.value})}>
                <option value="">-</option><option>Yes</option><option>No</option>
              </select>
            </Field>
            <Field label="Type of contact">
              <select className="cesco-input" value={f.type_of_contact||""} onChange={e=>setF({...f, type_of_contact:e.target.value})}>
                <option value="">-</option>{meta?.type_of_contact.map(r => <option key={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Assigned rep" full>
              <select className="cesco-input" value={f.assigned_rep_id||""} onChange={e=>setF({...f, assigned_rep_id:e.target.value || null})}>
                <option value="">- (defaults to creator)</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </Field>
            <Field label="Product Segments" full>
              <div className="flex flex-wrap items-center gap-2 min-h-[38px] py-1">
                {(f.product_segments || []).length ? (
                  (f.product_segments || []).map(s => (
                    <span key={s} className={`pill ${s === "M" ? "pill-grey" : "pill-green"}`}>{s}</span>
                  ))
                ) : (
                  <span className="text-sm text-gray-400">None</span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Read-only here - edit segments on the <b>Specifics</b> tab.
              </div>
            </Field>
          </div>
        )}
      </div>
      <BriefSummary co={co} onSaved={reload} toast={toast}/>
    </div>
  );
}

function Field({ label, children, full, titleExtra }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="cesco-label flex items-center gap-1.5">
        {label}
        {titleExtra}
      </label>
      {children}
    </div>
  );
}
function InfoRow({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <dt className="cesco-label">{label}</dt>
      <dd className="text-gray-900">{children}</dd>
    </div>
  );
}

function BriefSummary({ co, onSaved, toast }) {
  const [text, setText] = useState(co.brief_summary || "");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  // If the company refreshes from the server (e.g. after another save),
  // pull the new brief_summary into our local edit buffer.
  useEffect(() => { setText(co.brief_summary || ""); }, [co.brief_summary]);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/companies/${co.id}`, { brief_summary: text });
      toast.success("Summary on Company saved");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(toastErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="cesco-card p-5" data-testid="brief-summary-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">Summary on Company</h3>
        {!editing && (
          <button className="cesco-btn-ghost flex items-center gap-2 text-sm"
                  onClick={() => setEditing(true)}
                  data-testid="brief-summary-edit">
            <Edit3 size={14}/> Edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea className="cesco-input" rows={5}
                    placeholder="A short summary of this company that the whole team can see."
                    value={text} onChange={e=>setText(e.target.value)}
                    data-testid="brief-summary-input"
                    autoFocus/>
          <div className="flex justify-end gap-2">
            <button className="cesco-btn-ghost" onClick={() => { setText(co.brief_summary || ""); setEditing(false); }}>
              Cancel
            </button>
            <button className="cesco-btn-primary" onClick={save} disabled={saving}
                    data-testid="brief-summary-save">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      ) : (
        <div className="text-[15px] text-gray-900 whitespace-pre-wrap min-h-[3rem]"
             data-testid="brief-summary-display">
          {co.brief_summary?.trim()
            ? co.brief_summary
            : <span className="text-gray-400 italic">No summary yet - click Edit to add one.</span>}
        </div>
      )}
    </div>
  );
}

// --- Tab: Contacts ---------------------------------------------------------
function Contacts({ companyId, meta, toast, contacts, reloadContacts, users, tasksVersion, onQuickTask, autoOpenNew = false, onAutoOpenConsumed }) {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saLog, setSaLog] = useState(null);
  const [saEdit, setSaEdit] = useState(null); // existing sales-activity row being edited
  const [saRefreshKey, setSaRefreshKey] = useState(0);
  const [openTasks, setOpenTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [completingTask, setCompletingTask] = useState(null);

  // Auto-open the New Contact sheet when arriving via ?new_contact=1
  // (e.g. from the "Create and add contact" flow on Companies page).
  useEffect(() => {
    if (autoOpenNew) {
      setEdit(null);
      setShow(true);
      onAutoOpenConsumed?.();
    }
    // Only re-fire when the autoOpen flag flips - intentionally ignore reloadContacts identity.
     
  }, [autoOpenNew]);

  async function reloadTasks() {
    try {
      const { data } = await api.get(`/companies/${companyId}/activities?limit=300`);
      setOpenTasks(data.filter(r => r.type === "task" && r.status === "open"));
      setCompletedTasks(data.filter(r => r.type === "task" && r.status === "completed"));
    } catch { /* polled per tab focus - silent to avoid console spam */ }
  }
  // reloadTasks reads companyId via closure - listed deps are the real triggers.
   
  useEffect(() => { reloadTasks(); }, [companyId, saRefreshKey, tasksVersion]);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="cesco-btn-primary flex items-center gap-2" onClick={()=>{setEdit(null); setShow(true);}}
                data-testid="btn-new-contact"><Plus size={16}/> Add contact</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {contacts.map(c => (
          <div key={c.id} className="cesco-card p-4" data-testid="contact-card">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold truncate">{c.contact_title ? c.contact_title + " " : ""}{c.first_name} {c.last_name}</div>
                <div className="text-sm text-gray-600 truncate">{c.position}</div>
                {c.decision_maker === "Yes" && <span className="pill pill-green mt-1 inline-block">Decision maker</span>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button className="cesco-btn-ghost text-xs" onClick={()=>{setEdit(c); setShow(true);}}
                        data-testid="btn-edit-contact">Edit</button>
                {user?.role === "admin" && (
                  <button className="text-xs text-red-600 hover:underline"
                          onClick={async()=>{
                            const label = `${c.first_name || ""} ${c.last_name || ""}`.trim() || "this contact";
                            if (!confirm(`Delete ${label}? This also removes any tasks and sales activities linked to them. This cannot be undone.`)) return;
                            try {
                              const { data } = await api.delete(`/contacts/${c.id}`);
                              toast.success(`Contact deleted${data.deleted_activities ? ` (${data.deleted_activities} activities removed)` : ""}`);
                              reloadContacts();
                            } catch (e) { toast.error(toastErrorMessage(e)); }
                          }}
                          data-testid="btn-delete-contact">Delete</button>
                )}
              </div>
            </div>
            <div className="mt-2 space-y-1 text-sm">
              {c.direct_email && <a className="text-blue-700 hover:underline flex items-center gap-1" href={`mailto:${c.direct_email}`}><Mail size={12}/> {c.direct_email}</a>}
              {c.phone_mobile && <a className="text-blue-700 hover:underline flex items-center gap-1" href={telHref(c.phone_mobile)}><Phone size={12}/> {fmtPhone(c.phone_mobile)}</a>}
              {c.phone_office && <a className="text-blue-700 hover:underline flex items-center gap-1" href={telHref(c.phone_office)}><Phone size={12}/> {fmtPhone(c.phone_office)} (office)</a>}
              {c.linkedin_url && <a className="text-blue-700 hover:underline flex items-center gap-1 break-all" href={normaliseUrl(c.linkedin_url)} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin"><Linkedin size={12}/> LinkedIn</a>}
              {c.facebook_url && <a className="text-blue-700 hover:underline flex items-center gap-1 break-all" href={normaliseUrl(c.facebook_url)} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook"><Facebook size={12}/> Facebook</a>}
            </div>
            <ContactCustomerNotes contact={c} reload={reloadContacts} toast={toast}/>
            <ContactUpcomingTasks contact={c} tasks={openTasks.filter(t => t.contact_id === c.id)}
                                  onEdit={(t)=>setEditingTask(t)} onAdd={()=>onQuickTask?.(c)}
                                  onComplete={(t)=>setCompletingTask(t)}/>
            <ContactSalesActivities contact={c} toast={toast} refreshKey={saRefreshKey}
                                    completedTasks={completedTasks.filter(t => t.contact_id === c.id)}
                                    onAdd={()=>setSaLog(c)}
                                    onEdit={(row)=>setSaEdit({ contact: c, activity: row })}/>
          </div>
        ))}
        {contacts.length === 0 && <div className="text-sm text-gray-500 col-span-full">No contacts yet.</div>}
      </div>
      {show && <ContactForm meta={meta} initial={edit} companyId={companyId} onClose={()=>setShow(false)} onSaved={reloadContacts} toast={toast}/>}
      {saLog && <SalesActivityModal contact={saLog} companyId={companyId}
                                    onClose={()=>setSaLog(null)}
                                    onSaved={()=>setSaRefreshKey(k=>k+1)}
                                    onAddTask={(c)=>onQuickTask?.(c)}
                                    toast={toast}/>}
      {saEdit && <SalesActivityModal contact={saEdit.contact} companyId={companyId}
                                     existing={saEdit.activity}
                                     onClose={()=>setSaEdit(null)}
                                     onSaved={()=>setSaRefreshKey(k=>k+1)}
                                     toast={toast}/>}
      {editingTask && (
        <TaskEditModal task={editingTask} users={users}
                       onClose={()=>setEditingTask(null)}
                       onSaved={()=>{ setEditingTask(null); reloadTasks(); }}/>
      )}
      {completingTask && (
        <CompleteTaskModal task={{...completingTask, company_name: ""}} users={users}
                           onClose={()=>setCompletingTask(null)}
                           onDone={()=>{ setCompletingTask(null); reloadTasks(); }}/>
      )}
    </div>
  );
}

function ContactUpcomingTasks({ contact, tasks, onEdit, onAdd, onComplete }) {
  const today = todayAestISO();
  return (
    <div className="mt-3 rounded-md border-l-4 border-blue-400 bg-blue-50/60 p-3" data-testid="contact-upcoming-tasks">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Upcoming tasks ({tasks.length})</div>
        <button className="cesco-btn-ghost text-xs flex items-center gap-1"
                onClick={onAdd}
                data-testid="btn-contact-add-task"><Plus size={12}/> Task</button>
      </div>
      <div className="mt-2 space-y-1.5">
        {tasks.length === 0 && <div className="text-xs text-gray-500 italic">No upcoming tasks for this contact.</div>}
        {tasks.map(t => {
          const overdue = t.due_date < today;
          return (
            <div key={t.id} className={`text-sm border-l-2 pl-2 py-1 rounded-r bg-white/70 ${overdue ? "border-red-400" : "border-blue-300"}`}
                 data-testid="contact-upcoming-task">
              <div className="flex items-center gap-2 flex-wrap">
                {t.task_type && <span className={`pill ${taskTypePillClass(t.task_type)} text-xs`}>{t.task_type}</span>}
                <span className="text-xs text-gray-600">Due {fmtAestDate(t.due_date)}{t.due_time ? ` ${t.due_time}` : ""}</span>
                {overdue && <span className="pill pill-red text-xs">OVERDUE</span>}
                <span className="text-xs text-gray-500">· {t.assigned_to_name || "-"}</span>
                <div className="ml-auto flex gap-1">
                  <button className="text-xs text-blue-700 hover:text-blue-900" onClick={()=>onEdit(t)}
                          data-testid="contact-task-edit">Edit</button>
                  <button className="text-xs text-green-700 hover:text-green-900" onClick={()=>onComplete(t)}
                          data-testid="contact-task-complete">Complete</button>
                </div>
              </div>
              <div className="text-gray-900 whitespace-pre-wrap mt-0.5">{t.summary}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContactSalesActivities({ contact, toast, refreshKey, completedTasks = [], onAdd, onEdit }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  async function reload() {
    try {
      const { data } = await api.get(`/contacts/${contact.id}/sales-activities`);
      setRows(data);
    } catch { /* refresh triggered by parent - silent */ }
  }
  // reload reads contact.id via closure - listed deps are the real triggers.
   
  useEffect(() => { reload(); }, [contact.id, refreshKey]);
  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await api.delete(`/sales-activity/${pendingDelete.id}`);
      toast.success("Deleted"); reload();
    } catch(e) { toast.error(toastErrorMessage(e)); }
    finally { setPendingDelete(null); }
  }

  // Merge sales_activity rows + completed tasks into a single feed, sorted by date desc.
  const merged = [
    ...rows.map(r => ({ ...r, _kind: "sa", _date: r.activity_date || (r.created_at || "").slice(0,10) })),
    ...completedTasks.map(t => ({ ...t, _kind: "done_task", _date: (t.completed_at || t.due_date || "").slice(0,10) })),
  ].sort((a, b) => (b._date || "").localeCompare(a._date || ""));

  return (
    <div className="mt-3 rounded-md border-l-4 border-amber-400 bg-amber-50/70 p-3" data-testid="contact-sales-activities">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Sales activities ({merged.length})</div>
        <button className="cesco-btn-ghost text-xs flex items-center gap-1"
                onClick={onAdd}
                data-testid="btn-contact-add-sales-activity"><Plus size={12}/> Sales activity</button>
      </div>
      <div className="mt-2 space-y-1.5">
        {merged.length === 0 && <div className="text-xs text-gray-500 italic">No sales activities yet - click &quot;+ Sales activity&quot; to log one.</div>}
        {merged.map(r => {
          if (r._kind === "done_task") {
            return (
              <div key={`t-${r.id}`} className="text-sm border-l-2 border-green-400 pl-2 py-1 bg-white/70 rounded-r"
                   data-testid="contact-completed-task">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="pill pill-green text-xs">✓ Completed task</span>
                  {r.task_type && <span className={`pill ${taskTypePillClass(r.task_type)} text-xs`}>{r.task_type}</span>}
                  <span className="text-xs text-gray-500">{fmtAestDate(r._date)}</span>
                  <span className="text-xs text-gray-500">· by {r.completed_by_name || r.created_by_name || "-"}</span>
                </div>
                <div className="text-gray-900 whitespace-pre-wrap mt-0.5">{r.summary}</div>
                {r.outcome && <div className="text-xs text-gray-700 mt-0.5"><span className="font-medium">Outcome:</span> {r.outcome}</div>}
              </div>
            );
          }
          const canDel = user?.role === "admin" || user?.id === r.created_by_id;
          const canEdit = canDel;
          return (
            <div key={`sa-${r.id}`} className="text-sm border-l-2 border-amber-300 pl-2 py-1 bg-white/70 rounded-r">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="pill pill-yellow text-xs">{r.task_type || "Phone Call"}</span>
                <span className="text-xs text-gray-500">{fmtAestDate(r.activity_date)}</span>
                <span className="text-xs text-gray-500">· by {r.created_by_name || "-"}</span>
                {r.updated_at && (
                  <span className="text-xs text-gray-400" title={`Edited ${fmtAestDate(r.updated_at.slice(0,10))} by ${r.updated_by_name || ""}`}>· edited</span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {canEdit && (
                    <button className="text-xs text-blue-700 hover:text-blue-900"
                            onClick={()=>onEdit?.(r)}
                            data-testid="sales-activity-edit">Edit</button>
                  )}
                  {canDel && (
                    <button className="text-xs text-red-600 hover:text-red-800"
                            onClick={()=>setPendingDelete(r)}
                            data-testid="sales-activity-delete">Delete</button>
                  )}
                </div>
              </div>
              <div className="text-gray-900 whitespace-pre-wrap mt-0.5">{r.summary}</div>
            </div>
          );
        })}
      </div>
      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="sales-activity-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this sales activity?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.summary ? `"${pendingDelete.summary.slice(0, 120)}${pendingDelete.summary.length > 120 ? "…" : ""}"` : ""}
              <div className="mt-2 text-xs text-gray-500">This cannot be undone.</div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sales-activity-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}
                               className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
                               data-testid="sales-activity-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SalesActivityModal({ contact, companyId, onClose, onSaved, onAddTask, toast, existing = null }) {
  const today = todayAestISO();
  const isEdit = !!existing;
  const [f, setF] = useState({
    summary: existing?.summary || "",
    task_type: existing?.task_type || "Phone Call",
    activity_date: existing?.activity_date || today,
  });
  const [saving, setSaving] = useState(false);
  async function save({ thenAddTask = false } = {}) {
    if (!f.summary.trim() || !f.activity_date) {
      toast.error("Summary and date are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/sales-activity/${existing.id}`, {
          summary: f.summary.trim(),
          task_type: f.task_type,
          activity_date: f.activity_date,
        });
        toast.success("Sales activity updated");
      } else {
        await api.post(`/companies/${companyId}/sales-activity`, {
          summary: f.summary.trim(),
          task_type: f.task_type,
          activity_date: f.activity_date,
          contact_id: contact.id,
        });
        toast.success("Sales activity logged");
      }
      onSaved?.();
      onClose();
      if (thenAddTask && !isEdit) onAddTask?.(contact);
    } catch (e) { toast.error(toastErrorMessage(e)); }
    finally { setSaving(false); }
  }
  return (
    <Sheet open onClose={onClose}
           title={isEdit
             ? `Edit sales activity for ${contact.first_name} ${contact.last_name}`
             : `Log sales activity for ${contact.first_name} ${contact.last_name}`}
           footer={<div className="flex gap-2 justify-end flex-wrap">
             <button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
             {!isEdit && (
               <button className="cesco-btn-ghost" onClick={()=>save({ thenAddTask: true })} disabled={saving}
                       data-testid="sales-activity-save-and-task">{saving ? "Saving…" : "Save and add Task"}</button>
             )}
             <button className="cesco-btn-primary" onClick={()=>save()} disabled={saving}
                     data-testid="sales-activity-save">{saving ? "Saving…" : "Save"}</button>
           </div>}>
      <div className="space-y-3">
        <div className="text-sm text-gray-600">
          {isEdit
            ? "Edit the captured details. Author and timestamp are preserved."
            : "A record of something that already happened (no due date)."}
        </div>
        <div>
          <label className="cesco-label">Type *</label>
          <select className="cesco-input" value={f.task_type}
                  onChange={e=>setF({...f, task_type: e.target.value})}
                  data-testid="sales-activity-type">
            {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="cesco-label">Date *</label>
          <input type="date" className="cesco-input" value={f.activity_date}
                 max={today}
                 onChange={e=>setF({...f, activity_date: e.target.value})}
                 data-testid="sales-activity-date"/>
          <div className="text-xs text-gray-500 mt-1">Usually today or a backdate. Cannot be in the future.</div>
        </div>
        <div>
          <label className="cesco-label">Summary *</label>
          <textarea className="cesco-input" rows={3}
                    placeholder="What was discussed / what happened…"
                    value={f.summary}
                    onChange={e=>setF({...f, summary: e.target.value})}
                    data-testid="sales-activity-summary"/>
        </div>
      </div>
    </Sheet>
  );
}

function ContactCustomerNotes({ contact, reload, toast }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(contact.customer_notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(contact.customer_notes || ""); }, [contact.id, contact.customer_notes]);
  async function save() {
    setSaving(true);
    try {
      await api.patch(`/contacts/${contact.id}`, {
        first_name: contact.first_name, last_name: contact.last_name,
        customer_notes: text,
      });
      toast.success("Summary on Customer saved");
      setEditing(false);
      reload();
    } catch(e) { toast.error(toastErrorMessage(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="mt-3 rounded-md border-l-4 border-slate-400 bg-slate-50/70 p-3" data-testid="contact-customer-notes">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Summary on Customer</div>
        {!editing && (
          <button className="cesco-btn-ghost text-xs flex items-center gap-1"
                  onClick={()=>{ setText(contact.customer_notes || ""); setEditing(true); }}
                  data-testid="customer-notes-edit"><Edit3 size={12}/> Edit</button>
        )}
      </div>
      {!editing ? (
        <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
          {contact.customer_notes
            ? contact.customer_notes
            : <span className="text-gray-400 italic">No customer notes yet.</span>}
        </div>
      ) : (
        <div className="mt-1">
          <textarea className="cesco-input text-sm" rows={4}
                    placeholder="Hobbies, best times to visit, preferences…"
                    value={text} onChange={e=>setText(e.target.value)}
                    data-testid="customer-notes-input"/>
          <div className="flex gap-2 justify-end mt-1">
            <button className="cesco-btn-ghost text-sm" onClick={()=>setEditing(false)}
                    data-testid="customer-notes-cancel">Cancel</button>
            <button className="cesco-btn-accent text-sm" onClick={save} disabled={saving}
                    data-testid="customer-notes-save">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactForm({ meta, initial, companyId, onClose, onSaved }) {
  const [f, setF] = useState(initial || {
    contact_title:"", first_name:"", last_name:"", position:"", direct_email:"",
    phone_office:"", phone_mobile:"", linkedin_url:"", facebook_url:"", decision_maker:"",
  });
  const [err, setErr] = useState("");
  async function save() {
    try {
      if (initial) await api.patch(`/contacts/${initial.id}`, f);
      else await api.post(`/companies/${companyId}/contacts`, f);
      onSaved(); onClose();
    } catch(e) { setErr(e.response?.data?.detail || e.message); }
  }
  return (
    <Sheet open={true} onClose={onClose} title={initial ? "Edit contact" : "New contact"} widthClass="max-w-lg"
           footer={<div className="flex gap-2 justify-end"><button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
                      <button className="cesco-btn-primary" onClick={save} data-testid="contact-save">Save</button></div>}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Title">
          <select className="cesco-input" value={f.contact_title||""} onChange={e=>setF({...f, contact_title:e.target.value})}>
            <option value="">-</option>{meta?.contact_titles.map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Decision maker">
          <select className="cesco-input" value={f.decision_maker||""} onChange={e=>setF({...f, decision_maker:e.target.value})}>
            <option value="">-</option><option>Yes</option><option>No</option>
          </select>
        </Field>
        <Field label="First name *"><input className="cesco-input" required maxLength={50} value={f.first_name||""} onChange={e=>setF({...f, first_name:e.target.value})}/></Field>
        <Field label="Last name *"><input className="cesco-input" required maxLength={50} value={f.last_name||""} onChange={e=>setF({...f, last_name:e.target.value})}/></Field>
        <Field label="Position" full><input className="cesco-input" maxLength={80} value={f.position||""} onChange={e=>setF({...f, position:e.target.value})}/></Field>
        <Field label="Direct email" full><input className="cesco-input" type="email" maxLength={254} value={f.direct_email||""} onChange={e=>setF({...f, direct_email:e.target.value})}/></Field>
        <Field label="Office phone"><input className="cesco-input" value={f.phone_office||""} onChange={e=>setF({...f, phone_office:e.target.value})}/></Field>
        <Field label="Mobile"><input className="cesco-input" value={f.phone_mobile||""} onChange={e=>setF({...f, phone_mobile:e.target.value})}/></Field>
        <Field label="LinkedIn URL" full><input className="cesco-input" type="url" placeholder="https://linkedin.com/in/..." value={f.linkedin_url||""} onChange={e=>setF({...f, linkedin_url:e.target.value})} data-testid="contact-linkedin-input"/></Field>
        <Field label="Facebook URL" full><input className="cesco-input" type="url" placeholder="https://facebook.com/..." value={f.facebook_url||""} onChange={e=>setF({...f, facebook_url:e.target.value})} data-testid="contact-facebook-input"/></Field>
        {err && <div className="col-span-2 text-sm text-red-600">{err}</div>}
      </div>
    </Sheet>
  );
}

// --- Tab: Specifics --------------------------------------------------------
function Specifics({ co, meta, reload, toast }) {
  const [f, setF] = useState({
    product_segments: co.product_segments || [],
    mixer_market_segment: co.mixer_market_segment || "",
    mixer_history_need: co.mixer_history_need || [],
    drill_market_segment: co.drill_market_segment || "",
    drill_history_need: co.drill_history_need || [],
    drill_hire: co.drill_hire || [],
  });
  const [saving, setSaving] = useState(false);
  useEffect(()=>{ setF({
    product_segments: co.product_segments || [],
    mixer_market_segment: co.mixer_market_segment || "",
    mixer_history_need: co.mixer_history_need || [],
    drill_market_segment: co.drill_market_segment || "",
    drill_history_need: co.drill_history_need || [],
    drill_hire: co.drill_hire || [],
  });}, [co]);

  function toggleSeg(code) {
    setF(s => ({ ...s,
      product_segments: s.product_segments.includes(code)
        ? s.product_segments.filter(x => x !== code)
        : [...s.product_segments, code],
    }));
  }
  function toggleInList(key, val) {
    setF(s => ({ ...s,
      [key]: (s[key] || []).includes(val) ? s[key].filter(x => x !== val) : [...(s[key]||[]), val],
    }));
  }
  async function save() {
    setSaving(true);
    try {
      await api.patch(`/companies/${co.id}`, f);
      toast.success("Specifics saved");
    } catch(e) { toast.error(toastErrorMessage(e)); }
    setSaving(false); reload();
  }

  const showMixer = f.product_segments.includes("M");
  const showDrill = f.product_segments.some(s => s !== "M");
  const theme = segmentsTheme(f.product_segments);

  return (
    <div data-theme={theme === "default" ? undefined : theme} className="space-y-4" data-testid="specifics-tab">
      <div className="cesco-card p-5">
        <h3 className="font-semibold mb-2">Product segments</h3>
        <p className="text-sm text-gray-500 mb-3">Select all that apply. Theme + fields below update automatically.</p>
        <div className="flex flex-wrap gap-2">
          {meta?.product_segments.map(s => {
            const active = f.product_segments.includes(s.code);
            const isM = s.code === "M";
            return (
              <button key={s.code} onClick={()=>toggleSeg(s.code)}
                className={`pill cursor-pointer ${active ? (isM ? "pill-grey" : "pill-green") : ""}`}
                style={!active ? { border: "1px dashed #B6BAC5", color:"#666", background:"#fff" } : {}}
                data-testid={`seg-${s.code}`}>{s.code} - {s.name}</button>
            );
          })}
        </div>
      </div>

      {showMixer && (
        <div className="cesco-card p-5" style={{ borderTop: "3px solid #AAABAA" }} data-testid="mixer-block">
          <h3 className="font-semibold mb-3">Mixer specifics</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="cesco-label">Market segment (Mixer)</label>
              <select className="cesco-input" value={f.mixer_market_segment}
                      onChange={e=>setF({...f, mixer_market_segment:e.target.value})}>
                <option value="">-</option>
                {meta?.mixer_market_segments.map(s => <option key={s.code} value={s.code}>{`${s.code} - ${s.name}`}</option>)}
              </select>
            </div>
            <div>
              <label className="cesco-label">Customer history / need (Mixer)</label>
              <div className="flex flex-wrap gap-2">
                {meta?.mixer_history_need.map(v => {
                  const active = f.mixer_history_need.includes(v);
                  return <button key={v} className={`pill cursor-pointer ${active?"pill-grey":""}`}
                                 style={!active ? { border:"1px dashed #B6BAC5", color:"#666", background:"#fff"} : {}}
                                 onClick={()=>toggleInList("mixer_history_need", v)}>{v}</button>;
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDrill && (
        <div className="cesco-card p-5" style={{ borderTop: "3px solid #2ECC71" }} data-testid="drill-block">
          <h3 className="font-semibold mb-3">Drill specifics</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="cesco-label">Market segment (Drill)</label>
              <select className="cesco-input" value={f.drill_market_segment}
                      onChange={e=>setF({...f, drill_market_segment:e.target.value})}>
                <option value="">-</option>
                {meta?.drill_market_segments.map(s => <option key={s.code} value={s.code}>{`${s.code} - ${s.name}`}</option>)}
              </select>
            </div>
            <div>
              <label className="cesco-label">Products Customer Hires</label>
              <div className="flex flex-wrap gap-2">
                {meta?.drill_hire.map(s => {
                  const active = f.drill_hire.includes(s.code);
                  return <button key={s.code} className={`pill cursor-pointer ${active?"pill-green":""}`}
                                 style={!active ? { border:"1px dashed #B6BAC5", color:"#666", background:"#fff"} : {}}
                                 onClick={()=>toggleInList("drill_hire", s.code)}>{s.code} - {s.name}</button>;
                })}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="cesco-label">Products Customer Uses</label>
              <div className="flex flex-wrap gap-2">
                {meta?.drill_history_need.map(v => {
                  const active = f.drill_history_need.includes(v);
                  return <button key={v} className={`pill cursor-pointer ${active?"pill-green":""}`}
                                 style={!active ? { border:"1px dashed #B6BAC5", color:"#666", background:"#fff"} : {}}
                                 onClick={()=>toggleInList("drill_history_need", v)}>{v}</button>;
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button className="cesco-btn-primary" onClick={save} disabled={saving} data-testid="specifics-save">
          {saving ? "Saving…" : "Save specifics"}
        </button>
      </div>
    </div>
  );
}

// --- Tab: Discovery (unified log + missing-topics chips) -------------------
function Discovery({ co, meta, toast }) {
  const segments = co.product_segments || [];
  const showMixer = segments.includes("M");
  const showDrill = segments.some(s => s !== "M");
  return (
    <div className="space-y-6" data-testid="discovery-tab">
      {!showMixer && !showDrill && (
        <div className="cesco-card p-5 text-sm text-gray-500">
          Select a product segment under the <b>Specifics</b> tab to unlock discovery logs.
        </div>
      )}
      {showMixer && <DiscoveryLog companyId={co.id} segment="mixer"
        topics={meta?.mixer_discovery_topics || []} amountTopics={new Set(meta?.mixer_amount_topics || [])}
        label="Mixer Discovery Log" accent="#AAABAA" toast={toast}/>}
      {showDrill && <DrillDiscoveryTable companyId={co.id}
        topics={meta?.drill_discovery_topics || []}
        amountTopics={new Set(meta?.drill_amount_topics || [])}
        yesnoTopics={new Set(meta?.drill_yesno_topics || [])}
        toast={toast}/>}
    </div>
  );
}

function DiscoveryLog({ companyId, segment, topics, amountTopics, label, accent, toast }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filterTopic, setFilterTopic] = useState("");
  const [show, setShow] = useState(false);
  const [preset, setPreset] = useState("");
  const [editing, setEditing] = useState(null);

  async function reload() {
    const { data } = await api.get(`/companies/${companyId}/discovery?segment=${segment}`);
    setRows(data);
  }
  useEffect(() => { reload(); }, [companyId, segment]);

  async function handleDelete(entry) {
    if (!confirm(`Delete this ${entry.topic} entry? This cannot be undone.`)) return;
    try {
      await api.delete(`/discovery/${entry.id}`);
      toast.success("Entry deleted");
      reload();
    } catch(e) { toast.error(toastErrorMessage(e)); }
  }

  const capturedTopics = new Set(rows.map(r => r.topic));
  const missing = topics.filter(t => !capturedTopics.has(t));
  const topicOrder = new Map(topics.map((t, i) => [t, i]));
  const filtered = (filterTopic ? rows.filter(r => r.topic === filterTopic) : rows)
    .slice()
    .sort((a, b) => {
      const ai = topicOrder.has(a.topic) ? topicOrder.get(a.topic) : 999;
      const bi = topicOrder.has(b.topic) ? topicOrder.get(b.topic) : 999;
      if (ai !== bi) return ai - bi;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });

  return (
    <div className="cesco-card p-5" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">{label}</h3>
        <div className="flex items-center gap-2">
          <select className="cesco-input" value={filterTopic}
                  onChange={e=>setFilterTopic(e.target.value)}
                  data-testid={`${segment}-filter`}>
            <option value="">Filter by topic: all</option>
            {topics.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button className="cesco-btn-primary flex items-center gap-2"
                  onClick={()=>{setPreset(""); setShow(true);}}
                  data-testid={`${segment}-add-entry`}>
            <Plus size={14}/> Add entry
          </button>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="mt-3">
          <div className="text-xs text-gray-500 mb-2">Topics not yet captured - tap a chip to add:</div>
          <div className="flex flex-wrap gap-2">
            {missing.map(t => (
              <button key={t} className="discovery-chip" onClick={()=>{setPreset(t); setShow(true);}}
                      data-testid={`${segment}-chip-${t}`}>+ {t}</button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 max-h-[40rem] overflow-y-auto">
        {filtered.length === 0 && <div className="text-sm text-gray-500">No entries yet.</div>}
        {filtered.length > 0 && (
          <table className="w-full text-sm" data-testid={`${segment}-discovery-table`}>
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-2 py-1.5 w-28">Date</th>
                <th className="text-left px-2 py-1.5 w-32">Author</th>
                <th className="text-left px-2 py-1.5 w-44">Topic</th>
                <th className="text-left px-2 py-1.5">Entry</th>
                <th className="text-right px-2 py-1.5 w-24">Amount</th>
                <th className="px-2 py-1.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                const canDelete = user?.role === "admin" || user?.id === e.author_id;
                return (
                  <tr key={e.id} className="border-t align-top hover:bg-gray-50">
                    <td className="px-2 py-1.5 text-xs whitespace-nowrap">
                      {fmtAestDate(e.created_at)}
                      {e.edited_at && (
                        <div className="text-[10px] italic text-gray-400">edited</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-xs">{e.author_name || "-"}</td>
                    <td className="px-2 py-1.5"><span className="pill pill-blue text-xs">{e.topic}</span></td>
                    <td className="px-2 py-1.5 whitespace-pre-wrap">{e.text}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap text-xs">
                      {amountTopics.has(e.topic) && e.amount != null ? fmtAud(e.amount) : ""}
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button className="text-xs text-gray-600 hover:text-black mr-2"
                              onClick={()=>setEditing(e)}>Edit</button>
                      {canDelete && (
                        <button className="text-xs text-red-600"
                                onClick={()=>handleDelete(e)}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {show && (
        <DiscoveryEntryForm companyId={companyId} segment={segment} topics={topics}
          amountTopics={amountTopics} presetTopic={preset}
          onClose={()=>setShow(false)} onSaved={reload} toast={toast}/>
      )}
      {editing && (
        <DiscoveryEntryForm companyId={companyId} segment={segment} topics={topics}
          amountTopics={amountTopics} initial={editing}
          onClose={()=>setEditing(null)} onSaved={reload} toast={toast}/>
      )}
    </div>
  );
}

function DiscoveryEntryForm({ companyId, segment, topics, amountTopics, presetTopic, initial = null, onClose, onSaved, toast }) {
  const isEdit = !!initial;
  const [topic, setTopic] = useState(initial?.topic || presetTopic || topics[0] || "");
  const [text, setText] = useState(initial?.text || "");
  const [amount, setAmount] = useState(initial?.amount != null ? String(initial.amount) : "");
  const [err, setErr] = useState("");
  const showAmount = amountTopics.has(topic);
  async function save() {
    setErr("");
    if (!text.trim()) { setErr("Entry text is required"); return; }
    try {
      if (isEdit) {
        await api.patch(`/discovery/${initial.id}`, {
          topic, text: text.trim(),
          amount: showAmount && amount ? Number(amount) : null,
        });
        toast.success("Discovery entry updated");
      } else {
        await api.post(`/companies/${companyId}/discovery`, {
          segment, topic, text: text.trim(),
          amount: showAmount && amount ? Number(amount) : null,
        });
        toast.success("Discovery entry saved");
      }
      onSaved(); onClose();
    } catch(e) {
      const m = toastErrorMessage(e); setErr(m); toast.error(m);
    }
  }
  return (
    <Sheet open onClose={onClose} title={isEdit ? `Edit ${segment} discovery entry` : `New ${segment} discovery entry`}
           footer={<div className="flex gap-2 justify-end"><button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
                      <button className="cesco-btn-primary" onClick={save} data-testid="discovery-save">Save</button></div>}>
      <div className="space-y-3">
        <Field label="Topic *" full>
          {isEdit ? (
            <div className="cesco-input bg-gray-50 text-gray-700 cursor-not-allowed"
                 data-testid="discovery-topic-readonly">{topic}</div>
          ) : (
            <select className="cesco-input" value={topic} onChange={e=>setTopic(e.target.value)}
                    data-testid="discovery-topic">
              {topics.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
        </Field>
        <Field label="Entry text *" full>
          <textarea className="cesco-input" rows={5} value={text} onChange={e=>setText(e.target.value)}
                    data-testid="discovery-text"/>
        </Field>
        {showAmount && (
          <Field label="Amount (AUD)" full>
            <input className="cesco-input" type="number" step="0.01" value={amount}
                   onChange={e=>setAmount(e.target.value)} data-testid="discovery-amount"/>
          </Field>
        )}
        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>
    </Sheet>
  );
}

// --- Drill Discovery - fixed-order Q&A table (one answer per question) -----
function DrillDiscoveryTable({ companyId, topics, amountTopics, yesnoTopics, toast }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // { topic, entry|null }

  async function reload() {
    const { data } = await api.get(`/companies/${companyId}/discovery?segment=drill`);
    setRows(data);
  }
  useEffect(() => { reload(); }, [companyId]);

  // Index latest entry per topic
  const byTopic = new Map();
  for (const r of rows) {
    const cur = byTopic.get(r.topic);
    if (!cur || (r.created_at || "") > (cur.created_at || "")) byTopic.set(r.topic, r);
  }

  async function clearAnswer(entry) {
    if (!confirm("Clear this answer? This cannot be undone.")) return;
    try {
      await api.delete(`/discovery/${entry.id}`);
      toast.success("Answer cleared");
      reload();
    } catch (e) { toast.error(toastErrorMessage(e)); }
  }

  return (
    <div className="cesco-card p-5" style={{ borderTop: "3px solid #2ECC71" }} data-testid="drill-discovery-table-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Drill Discovery</h3>
        <div className="text-xs text-gray-500">One answer per question. Click <b>Edit</b> to add or update.</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="drill-discovery-qa-table">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-2 py-1.5 w-12">#</th>
              <th className="text-left px-2 py-1.5">Question</th>
              <th className="text-left px-2 py-1.5">Answer</th>
              <th className="text-left px-2 py-1.5 w-44">Answered by</th>
              <th className="px-2 py-1.5 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {topics.map((t, i) => {
              const e = byTopic.get(t);
              const canDelete = e && (user?.role === "admin" || user?.id === e.author_id);
              const isAmount = amountTopics.has(t);
              const isYesNo = yesnoTopics.has(t);
              return (
                <tr key={t} className="border-t align-top hover:bg-gray-50" data-testid={`drill-qa-row-${i+1}`}>
                  <td className="px-2 py-2 text-xs text-gray-500 whitespace-nowrap">{i + 1}</td>
                  <td className="px-2 py-2 font-medium text-gray-800">{t}</td>
                  <td className="px-2 py-2 whitespace-pre-wrap text-gray-900">
                    {e ? (
                      <>
                        {isAmount && e.amount != null ? fmtAud(e.amount) : null}
                        {isAmount && e.amount != null && e.text ? " - " : null}
                        {isYesNo ? <span className={`pill ${e.text === "Yes" ? "pill-green" : "pill-gray"} text-xs mr-1`}>{e.text}</span> : e.text}
                      </>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">
                    {e ? <>{e.author_name || "-"}<div className="text-[11px] text-gray-400">{fmtAestDate(e.created_at)}</div></> : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <button className="text-xs text-gray-700 hover:text-black mr-2 underline"
                            onClick={()=>setEditing({ topic: t, entry: e || null })}
                            data-testid={`drill-qa-edit-${i+1}`}>{e ? "Edit" : "Answer"}</button>
                    {e && canDelete && (
                      <button className="text-xs text-red-600 hover:underline"
                              onClick={()=>clearAnswer(e)}
                              data-testid={`drill-qa-clear-${i+1}`}>Clear</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && (
        <DrillAnswerSheet companyId={companyId} topic={editing.topic} initial={editing.entry}
                          isAmount={amountTopics.has(editing.topic)}
                          isYesNo={yesnoTopics.has(editing.topic)}
                          onClose={()=>setEditing(null)}
                          onSaved={()=>{ setEditing(null); reload(); }}
                          toast={toast}/>
      )}
    </div>
  );
}

function DrillAnswerSheet({ companyId, topic, initial, isAmount, isYesNo, onClose, onSaved, toast }) {
  const [text, setText] = useState(initial?.text || (isYesNo ? "" : ""));
  const [amount, setAmount] = useState(initial?.amount != null ? String(initial.amount) : "");
  const [err, setErr] = useState("");
  async function save() {
    setErr("");
    if (isYesNo) {
      if (text !== "Yes" && text !== "No") { setErr("Please select Yes or No"); return; }
    } else if (isAmount) {
      if (!amount) { setErr("Please enter an amount"); return; }
    } else if (!text.trim()) {
      setErr("Answer is required"); return;
    }
    try {
      await api.post(`/companies/${companyId}/discovery`, {
        segment: "drill", topic,
        text: isAmount ? (text.trim() || "") : (isYesNo ? text : text.trim()),
        amount: isAmount && amount ? Number(amount) : null,
      });
      toast.success("Answer saved");
      onSaved();
    } catch (e) {
      const m = toastErrorMessage(e); setErr(m); toast.error(m);
    }
  }
  return (
    <Sheet open onClose={onClose} title={topic} widthClass="max-w-lg"
           footer={<div className="flex gap-2 justify-end">
             <button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
             <button className="cesco-btn-primary" onClick={save} data-testid="drill-qa-save">Save</button>
           </div>}>
      <div className="space-y-3">
        {isYesNo && (
          <Field label="Answer *" full>
            <select className="cesco-input" value={text} onChange={e=>setText(e.target.value)}
                    data-testid="drill-qa-yesno">
              <option value="">- Select -</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </Field>
        )}
        {isAmount && (
          <>
            <Field label="Amount (AUD) *" full>
              <input className="cesco-input" type="number" step="0.01" value={amount}
                     onChange={e=>setAmount(e.target.value)} data-testid="drill-qa-amount"/>
            </Field>
            <Field label="Notes (optional)" full>
              <textarea className="cesco-input" rows={3} value={text}
                        onChange={e=>setText(e.target.value)} data-testid="drill-qa-notes"/>
            </Field>
          </>
        )}
        {!isYesNo && !isAmount && (
          <Field label="Answer *" full>
            <textarea className="cesco-input" rows={5} value={text}
                      onChange={e=>setText(e.target.value)} data-testid="drill-qa-text"/>
          </Field>
        )}
        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>
    </Sheet>
  );
}


// --- Tab: Equipment --------------------------------------------------------
function Equipment({ companyId, toast }) {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  async function reload() {
    const { data } = await api.get(`/companies/${companyId}/equipment`);
    setRows(data);
  }
  useEffect(() => { reload(); }, [companyId]);
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="cesco-btn-primary flex items-center gap-2" onClick={()=>{setEdit(null); setShow(true);}}
                data-testid="btn-new-equipment"><Plus size={16}/> Add equipment</button>
      </div>
      <div className="cesco-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Segment</th>
                <th className="text-left px-3 py-2">Manufacturer</th>
                <th className="text-left px-3 py-2">Model</th><th className="text-left px-3 py-2">Cesco Serial Number</th>
                <th className="text-left px-3 py-2">Mfd</th><th className="text-left px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-gray-500">No equipment yet</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t" data-testid="equipment-row">
                <td className="px-3 py-2 font-medium">{r.item}<div className="text-xs text-gray-500">{r.specific_features}</div></td>
                <td className="px-3 py-2">{r.product_segment ? <span className="pill pill-blue text-xs">{r.product_segment}</span> : <span className="text-gray-400">-</span>}</td>
                <td className="px-3 py-2">{r.manufacturer}</td>
                <td className="px-3 py-2">{r.model}</td>
                <td className="px-3 py-2">{r.serial_number}</td>
                <td className="px-3 py-2">{r.date_of_manufacture}</td>
                <td className="px-3 py-2"><button className="text-gray-600 hover:text-black text-xs"
                    onClick={()=>{setEdit(r); setShow(true);}}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && <EquipmentForm initial={edit} companyId={companyId} onClose={()=>setShow(false)} onSaved={reload} toast={toast}/>}
    </div>
  );
}

// MonthYearPicker - two side-by-side selects (instant year jump, mobile-friendly).
// Stores back to "MM/YYYY" string; tolerant of empty/legacy values.
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function MonthYearPicker({ value, onChange }) {
  const m = String(value || "").match(/^(\d{1,2})\/(\d{4})$/);
  const month = m ? m[1].padStart(2, "0") : "";
  const year  = m ? m[2] : "";
  const thisYear = new Date().getFullYear();
  const years = [];
  for (let y = thisYear; y >= thisYear - 60; y--) years.push(String(y));
  function commit(nextMonth, nextYear) {
    if (nextMonth && nextYear) onChange(`${nextMonth}/${nextYear}`);
    else if (!nextMonth && !nextYear) onChange("");
    // If only one side is set, do not commit yet - wait for the other.
  }
  return (
    <div className="flex gap-2" data-testid="equipment-dom">
      <select className="cesco-input" value={month}
              onChange={e=>commit(e.target.value, year)}
              data-testid="equipment-dom-month" aria-label="Month">
        <option value="">Month</option>
        {MONTH_NAMES.map((nm, i) => {
          const code = String(i + 1).padStart(2, "0");
          return <option key={code} value={code}>{code} - {nm}</option>;
        })}
      </select>
      <select className="cesco-input" value={year}
              onChange={e=>commit(month, e.target.value)}
              data-testid="equipment-dom-year" aria-label="Year">
        <option value="">Year</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

function EquipmentForm({ initial, companyId, onClose, onSaved, toast }) {
  const { meta } = useAuth();
  const [f, setF] = useState(initial || {
    item:"", manufacturer:"", model:"", specific_features:"", date_of_manufacture:"",
    serial_number:"", equipment_type:"", customer_reference_number:"",
    product_segment:"",
  });
  const [err, setErr] = useState("");
  async function save() {
    try {
      if (initial) {
        await api.patch(`/equipment/${initial.id}`, f);
        toast.success("Equipment updated");
      } else {
        await api.post(`/companies/${companyId}/equipment`, f);
        toast.success("Equipment added");
      }
      onSaved(); onClose();
    } catch(e) {
      const m = toastErrorMessage(e); setErr(m); toast.error(m);
    }
  }
  return (
    <Sheet open onClose={onClose} title={initial ? "Edit equipment" : "Add equipment"}
           footer={<div className="flex gap-2 justify-end"><button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
                      <button className="cesco-btn-primary" onClick={save} data-testid="equipment-save">Save</button></div>}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Item *" full><input className="cesco-input" value={f.item} required onChange={e=>setF({...f, item:e.target.value})}/></Field>
        <Field label="Product segment" full>
          <select className="cesco-input" value={f.product_segment||""}
                  onChange={e=>setF({...f, product_segment:e.target.value})}
                  data-testid="equipment-segment">
            <option value="">- Select -</option>
            {meta?.product_segments?.map(s => (
              <option key={s.code} value={s.code}>{`${s.code} - ${s.name}`}</option>
            ))}
          </select>
        </Field>
        <Field label="Manufacturer"><input className="cesco-input" value={f.manufacturer||""} onChange={e=>setF({...f, manufacturer:e.target.value})}/></Field>
        <Field label="Model"><input className="cesco-input" value={f.model||""} onChange={e=>setF({...f, model:e.target.value})}/></Field>
        <Field label="Specific features" full><input className="cesco-input" value={f.specific_features||""} onChange={e=>setF({...f, specific_features:e.target.value})}/></Field>
        <Field label="Date of manufacture">
          <MonthYearPicker value={f.date_of_manufacture}
                           onChange={v=>setF({...f, date_of_manufacture: v})}/>
        </Field>
        <Field label="Cesco Serial Number"><input className="cesco-input" value={f.serial_number||""} onChange={e=>setF({...f, serial_number:e.target.value})}/></Field>
        <Field label="Type (Mixer/Drill/Attachment)"><input className="cesco-input" value={f.equipment_type||""} onChange={e=>setF({...f, equipment_type:e.target.value})}/></Field>
        <Field label="Customer reference #"><input className="cesco-input" value={f.customer_reference_number||""} onChange={e=>setF({...f, customer_reference_number:e.target.value})}/></Field>
        {err && <div className="col-span-2 text-sm text-red-600">{err}</div>}
      </div>
    </Sheet>
  );
}

// --- Tab: Activity (feed + tasks + notes) ----------------------------------
function Activity({ companyId, users, toast, contacts }) {
  const [rows, setRows] = useState([]);
  const [showTask, setShowTask] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [editing, setEditing] = useState(null);
  async function reload() {
    const { data } = await api.get(`/companies/${companyId}/activities?limit=200`);
    setRows(data);
  }
  useEffect(() => { reload(); }, [companyId]);

  const today = todayAestISO();
  const openTasks = rows.filter(r => r.type === "task" && r.status === "open");
  const others = rows.filter(r => !(r.type === "task" && r.status === "open"));

  async function toggleTask(t) {
    if (t.status === "open") { setCompleting(t); return; }
    try {
      await api.patch(`/tasks/${t.id}`, { status: "open" });
      toast.success("Task reopened");
      reload();
    } catch(e) { toast.error(toastErrorMessage(e)); }
  }

  return (
    <div className="space-y-3" data-testid="activity-tab">
      <div className="flex gap-2 justify-end">
        <button className="cesco-btn-primary flex items-center gap-2" onClick={()=>setShowTask(true)}
                data-testid="btn-add-task"><Plus size={16}/> Add task</button>
        <button className="cesco-btn-accent" onClick={()=>setShowNote(true)}
                data-testid="btn-add-note">Add note</button>
      </div>

      {openTasks.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Pinned open tasks</div>
          <div className="space-y-2">
            {openTasks.map(t => (
              <div key={t.id} className="feed-item-task-open p-3 rounded-md flex items-start gap-3" data-testid="open-task">
                <input type="checkbox" className="mt-1" onChange={()=>toggleTask(t)}/>
                <div className="flex-1">
                  <div className="font-medium">{t.summary}</div>
                  <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-2 gap-y-1 items-center">
                    {t.task_type && <span className={`pill ${taskTypePillClass(t.task_type)}`} data-testid="task-type-pill">{t.task_type}</span>}
                    <span>Due {fmtAestDate(t.due_date)}{t.due_time ? ` ${t.due_time}` : ""}</span>
                    <span>· assigned to {t.assigned_to_name || "-"}</span>
                    {t.contact_name && <span className="pill pill-blue">👤 {t.contact_name}</span>}
                    {t.due_date < today && <span className="pill pill-red">OVERDUE</span>}
                  </div>
                </div>
                <button className="cesco-btn-ghost text-xs shrink-0" onClick={()=>setEditing(t)}
                        data-testid="btn-edit-task">Edit</button>
                <button className="cesco-btn-ghost text-xs shrink-0" onClick={()=>setCompleting(t)}
                        data-testid="btn-complete-task">Complete</button>
                <div className="text-xs text-gray-400 shrink-0 text-right hidden sm:block">
                  {t.created_by_name}<br/>{fmtAestDateTime(t.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {others.map(r => {
          if (r.type === "task") {
            return (
              <div key={r.id} className="feed-item-task-done p-3 rounded-md flex items-start gap-3" data-testid="done-task">
                <input type="checkbox" className="mt-1" checked readOnly/>
                <div className="flex-1">
                  <div className="font-medium line-through">{r.summary}</div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-2 gap-y-1 items-center mt-1">
                    {r.task_type && <span className={`pill ${taskTypePillClass(r.task_type)}`}>{r.task_type}</span>}
                    <span>Due {fmtAestDate(r.due_date)}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-400 shrink-0 text-right">
                  {r.created_by_name}<br/>{fmtAestDateTime(r.created_at)}
                </div>
              </div>
            );
          }
          if (r.type === "note") {
            return (
              <div key={r.id} className="feed-item-note p-3 rounded-md" data-testid="note">
                <div className="text-xs text-gray-500 mb-1">📝 NOTE · {r.created_by_name} · {fmtAestDateTime(r.created_at)}</div>
                <div className="whitespace-pre-wrap">{r.text}</div>
              </div>
            );
          }
          return (
            <div key={r.id} className="feed-item-system p-3 rounded-md text-sm" data-testid="system-event">
              ⚙ {r.message} - {r.created_by_name} · {fmtAestDateTime(r.created_at)}
            </div>
          );
        })}
        {rows.length === 0 && <div className="text-sm text-gray-500">No activity yet.</div>}
      </div>

      {showTask && <TaskForm companyId={companyId} users={users} contacts={contacts}
                              onClose={()=>setShowTask(false)} onSaved={reload} toast={toast}/>}
      {showNote && <NoteForm companyId={companyId} onClose={()=>setShowNote(false)} onSaved={reload} toast={toast}/>}
      {completing && (
        <CompleteTaskModal task={{...completing, company_name: ""}} users={users}
          onClose={()=>setCompleting(null)} onDone={reload}/>
      )}
      {editing && (
        <TaskEditModal task={editing} users={users}
          onClose={()=>setEditing(null)} onSaved={reload}/>
      )}
    </div>
  );
}

function TaskForm({ companyId, users, contacts = [], presetContactId = null, onClose, onSaved, toast }) {
  const [f, setF] = useState({
    summary:"", due_date:"", due_time:"",
    assigned_to_id:"",
    contact_id: presetContactId || "",
    task_type: "Other",
  });
  const [err, setErr] = useState("");
  async function save() {
    setErr("");
    if (!f.summary.trim() || !f.due_date) { setErr("Summary and due date are required"); return; }
    try {
      await api.post(`/companies/${companyId}/tasks`, {
        summary: f.summary.trim(), due_date: f.due_date, due_time: f.due_time || null,
        assigned_to_id: f.assigned_to_id || null,
        contact_id: f.contact_id || null,
        task_type: f.task_type || "Other",
      });
      toast.success("Task created");
      onSaved?.(); onClose();
    } catch(e) {
      const m = toastErrorMessage(e); setErr(m); toast.error(m);
    }
  }
  return (
    <Sheet open onClose={onClose} title="New task"
      footer={<div className="flex gap-2 justify-end"><button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
                  <button className="cesco-btn-primary" onClick={save} data-testid="task-save">Save</button></div>}>
      <div className="space-y-3">
        <Field label="Type *" full>
          <select className="cesco-input" value={f.task_type}
                  onChange={e=>setF({...f, task_type:e.target.value})} data-testid="task-type">
            {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Summary *" full>
          <textarea className="cesco-input" rows={3} value={f.summary}
                    onChange={e=>setF({...f, summary:e.target.value})} data-testid="task-summary"/>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Due date *"><input className="cesco-input" type="date" value={f.due_date} onChange={e=>setF({...f, due_date:e.target.value})} data-testid="task-due"/></Field>
          <Field label="Due time"><TimePicker value={f.due_time} onChange={v=>setF({...f, due_time:v})} testId="task-time"/></Field>
        </div>
        <Field label="Related contact (optional)" full>
          <select className="cesco-input" value={f.contact_id}
                  onChange={e=>setF({...f, contact_id:e.target.value})} data-testid="task-contact">
            <option value="">- (company-level task)</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{`${c.first_name} ${c.last_name}${c.position ? ` · ${c.position}` : ""}`}</option>)}
          </select>
        </Field>
        <Field label="Assign to" full>
          <select className="cesco-input" value={f.assigned_to_id} onChange={e=>setF({...f, assigned_to_id:e.target.value})}>
            <option value="">(me)</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>
    </Sheet>
  );
}

function NoteForm({ companyId, onClose, onSaved, toast }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  async function save() {
    if (!text.trim()) { setErr("Enter some text"); return; }
    try {
      await api.post(`/companies/${companyId}/notes`, { text: text.trim() });
      toast.success("Note added");
      onSaved(); onClose();
    } catch(e) { toast.error(toastErrorMessage(e)); }
  }
  return (
    <Sheet open onClose={onClose} title="New note"
      footer={<div className="flex gap-2 justify-end"><button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
                  <button className="cesco-btn-primary" onClick={save} data-testid="note-save">Save</button></div>}>
      <textarea className="cesco-input" rows={5} value={text} onChange={e=>setText(e.target.value)}
                placeholder="Log a call, email, meeting note…" data-testid="note-text"/>
      {err && <div className="text-sm text-red-600 mt-2">{err}</div>}
    </Sheet>
  );
}

// --- Tab: Quotes for this company ------------------------------------------
function CompanyQuotes({ companyId }) {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get(`/quotes?company_id=${companyId}`).then(r => setRows(r.data)); }, [companyId]);
  const today = todayAestISO();
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="cesco-btn-primary flex items-center gap-2"
                onClick={()=>nav(`/quotes/new?company_id=${companyId}`)}
                data-testid="btn-new-quote"><Plus size={16}/> New quote</button>
      </div>
      <div className="cesco-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600"><tr>
            <th className="text-left px-3 py-2">Quote #</th><th className="text-left px-3 py-2">Product</th>
            <th className="text-left px-3 py-2">Value</th><th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Reminder</th><th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-gray-500">No quotes yet</td></tr>}
            {rows.map(q => {
              const reminderToday = q.reminder_date === today;
              return (
                <tr key={q.id} className="border-t" data-testid="company-quote-row">
                  <td className="px-3 py-2 font-medium"><Link className="hover:underline" to={`/quotes/${q.id}`}>{q.quote_number}</Link></td>
                  <td className="px-3 py-2">{q.product_segment === "M" ? "Mixer" : "Drill"} · {q.product_category}</td>
                  <td className="px-3 py-2">{fmtAud(q.value_of_sale)}</td>
                  <td className="px-3 py-2"><StatusPill s={q.status}/></td>
                  <td className="px-3 py-2"><span className={reminderToday ? "reminder-flash" : ""}>{fmtAestDate(q.reminder_date)}</span></td>
                  <td className="px-3 py-2 text-right"><Link to={`/quotes/${q.id}`} className="text-xs text-gray-600 hover:text-black">Open →</Link></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const cls = s === "WON" ? "pill-green" : s === "LOST" ? "pill-red" : "pill-blue";
  return <span className={`pill ${cls}`}>{s}</span>;
}
